import { useMedplum } from '@medplum/react';
import { getReferenceString, resolveId } from '@medplum/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Practitioner, Reference, Schedule, Slot } from '@medplum/fhirtypes';
import {
  buildOnCallSlot,
  fetchOnCallSchedule,
  getDefaultOnCallTimes,
  getOnCallScheduleWindow,
  parseOnCallSlot,
} from '@bariendo/lib/on-call';
import { getBrowserTimezone } from '@/pages/SchedulingPage/utils/common';

export interface OnCallRowState {
  date: string;
  slotId?: string;
  primaryId: string | null;
  backupId: string | null;
  startTime: string;
  endTime: string;
}

export interface RowSaveState {
  saving: boolean;
  error?: string;
}

function buildDefaultRow(date: string, timezone: string): OnCallRowState {
  const { startTime, endTime } = getDefaultOnCallTimes(date, timezone);

  return { date, primaryId: null, backupId: null, startTime, endTime };
}

function rowFromSlot(slot: Slot, timezone: string): OnCallRowState {
  const parsed = parseOnCallSlot(slot, timezone);

  return {
    date: parsed.rowDate,
    slotId: parsed.slotId,
    primaryId: parsed.primaryRef ? (resolveId(parsed.primaryRef) ?? null) : null,
    backupId: parsed.backupRef ? (resolveId(parsed.backupRef) ?? null) : null,
    startTime: parsed.startTime,
    endTime: parsed.endTime,
  };
}

function canPersistRow(row: OnCallRowState): boolean {
  return !!row.primaryId && !!row.startTime && !!row.endTime;
}

export interface UseOnCallScheduleReturn {
  loading: boolean;
  error?: string;
  rows: OnCallRowState[];
  rowSaveStates: Record<string, RowSaveState>;
  /** The on-call Schedule, absent until loaded or when none exists and none may be created. */
  schedule: Schedule | undefined;
  /** Bumped after every successful write so dependent views can recompute. */
  revision: number;
  saveRow: (row: OnCallRowState) => Promise<void>;
  applyBulk: (
    dates: string[],
    primaryId: string | null,
    backupId: string | null,
    rowsByDate: Map<string, OnCallRowState>
  ) => Promise<void>;
}

export function useOnCallSchedule(options: { canEdit?: boolean } = {}): UseOnCallScheduleReturn {
  const { canEdit = false } = options;
  const medplum = useMedplum();
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [rows, setRows] = useState<OnCallRowState[]>([]);
  const [rowSaveStates, setRowSaveStates] = useState<Record<string, RowSaveState>>({});
  const [schedule, setSchedule] = useState<Schedule | undefined>(undefined);
  const [revision, setRevision] = useState(0);

  const scheduleRef = useRef<Schedule | undefined>(undefined);
  const slotsByDateRef = useRef<Map<string, Slot>>(new Map());

  const setRowSaveState = useCallback((date: string, state: RowSaveState) => {
    setRowSaveStates((prev) => ({ ...prev, [date]: state }));
  }, []);

  const persistRow = useCallback(
    async (row: OnCallRowState): Promise<void> => {
      // Defence in depth. The page already hides every control from a viewer, but "no
      // control is rendered" is the same argument that let the IVR pages offer writes that
      // 403'd, so the write path refuses on its own rather than trusting its caller.
      if (!canEdit) {
        throw new Error('You do not have permission to edit the on-call schedule');
      }

      const schedule = scheduleRef.current;
      if (!schedule) {
        throw new Error('On-call schedule not loaded');
      }

      const existingSlot = slotsByDateRef.current.get(row.date);

      if (!canPersistRow(row)) {
        if (existingSlot?.id) {
          await medplum.deleteResource('Slot', existingSlot.id);
          slotsByDateRef.current.delete(row.date);
        }

        setRows((prev) =>
          prev.map((current) =>
            current.date === row.date
              ? {
                  ...current,
                  slotId: undefined,
                  primaryId: null,
                  backupId: null,
                  ...getDefaultOnCallTimes(row.date, browserTimezone),
                }
              : current
          )
        );
        return;
      }

      const primaryId = row.primaryId;
      if (!primaryId) {
        return;
      }

      const primaryRef = {
        reference: getReferenceString({ resourceType: 'Practitioner', id: primaryId }),
      } as Reference<Practitioner>;
      const backupRef = row.backupId
        ? ({
            reference: getReferenceString({ resourceType: 'Practitioner', id: row.backupId }),
          } as Reference<Practitioner>)
        : undefined;

      const built = buildOnCallSlot(
        schedule,
        row.date,
        primaryRef,
        backupRef,
        row.startTime,
        row.endTime,
        browserTimezone,
        existingSlot
      );

      const saved = existingSlot?.id
        ? await medplum.updateResource({ ...built, id: existingSlot.id })
        : await medplum.createResource(built);

      slotsByDateRef.current.set(row.date, saved);
      // Totals shown above the table are derived from these slots, so they have to know.
      setRevision((current) => current + 1);

      setRows((prev) =>
        prev.map((current) =>
          current.date === row.date
            ? {
                ...row,
                slotId: saved.id,
              }
            : current
        )
      );
    },
    [medplum, browserTimezone, canEdit]
  );

  const saveRow = useCallback(
    async (row: OnCallRowState): Promise<void> => {
      setRowSaveState(row.date, { saving: true });

      try {
        await persistRow(row);
        setRowSaveState(row.date, { saving: false });
      } catch (saveError) {
        const message = saveError instanceof Error ? saveError.message : 'Failed to save on-call shift';
        setRowSaveState(row.date, { saving: false, error: message });
        throw saveError;
      }
    },
    [persistRow, setRowSaveState]
  );

  const applyBulk = useCallback(
    async (
      dates: string[],
      primaryId: string | null,
      backupId: string | null,
      rowsByDate: Map<string, OnCallRowState>
    ): Promise<void> => {
      await Promise.all(
        dates.map(async (date) => {
          const current = rowsByDate.get(date) ?? buildDefaultRow(date, browserTimezone);
          const nextRow: OnCallRowState = {
            ...current,
            primaryId,
            backupId: primaryId && backupId === primaryId ? null : backupId,
          };

          setRows((prev) => prev.map((row) => (row.date === date ? nextRow : row)));
          await saveRow(nextRow);
        })
      );
    },
    [saveRow, browserTimezone]
  );

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(undefined);

      try {
        // Reading must not create the Schedule for someone who cannot write it.
        const { schedule, timezone, slotsByDate } = await fetchOnCallSchedule(medplum, browserTimezone, {
          createIfMissing: canEdit,
        });
        scheduleRef.current = schedule;
        setSchedule(schedule);
        slotsByDateRef.current = slotsByDate;

        const { dates } = getOnCallScheduleWindow(timezone);
        const nextRows = dates.map((date) => {
          const slot = slotsByDate.get(date);
          return slot ? rowFromSlot(slot, timezone) : buildDefaultRow(date, timezone);
        });

        setRows(nextRows);
      } catch (loadError) {
        const message = loadError instanceof Error ? loadError.message : 'Failed to load on-call schedule';
        setError(message);
        setRows([]);
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, [medplum, browserTimezone, canEdit]);

  return {
    loading,
    error,
    rows,
    rowSaveStates,
    schedule,
    revision,
    saveRow,
    applyBulk,
  };
}
